import useTranslation from 'next-translate/useTranslation';
import { useMemo, useState } from 'react';

import TemplateCategoryFilter from '@/components/media/TemplateCategoryFilter';
import TemplateMasonry from '@/components/media/TemplateMasonry';

import {
  filterMediaTemplatesByCategory,
  getMediaTemplateCategories,
  getNewMediaTemplates,
  ImageTool,
  NEW_TEMPLATE_CATEGORY,
} from '@/constants/mediaTemplates';

interface TemplatesGalleryProps {
  templates: ImageTool[];
  /** Page namespace — older templates are only translated there. */
  fallbackNamespace: string;
  /** Templates whose uri leaves this path open in a new tab. Omit to always
   *  navigate in place. */
  currentPath?: string;
  /** Chip selected on mount. `NEW_TEMPLATE_CATEGORY` falls back to "All" when
   *  nothing is new. */
  defaultCategory?: string | null;
  /** Show this many tiles per page with a "Show more" button; omit to show
   *  everything at once. */
  pageSize?: number;
}

// Templates tab of the Image and Video generators and the dashboard's Get
// Inspired section: category chips (with a virtual "New" chip for recently
// added templates) + masonry.
const TemplatesGallery = ({
  templates,
  fallbackNamespace,
  currentPath,
  defaultCategory = null,
  pageSize,
}: TemplatesGalleryProps) => {
  const { t } = useTranslation(fallbackNamespace);
  const { t: tMedia } = useTranslation('media-templates');

  const newTemplates = useMemo(
    () => getNewMediaTemplates(templates),
    [templates],
  );
  const [category, setCategoryState] = useState<string | null>(() =>
    defaultCategory === NEW_TEMPLATE_CATEGORY && newTemplates.length === 0
      ? null
      : defaultCategory,
  );
  const [pagesShown, setPagesShown] = useState(1);
  const setCategory = (next: string | null) => {
    setCategoryState(next);
    setPagesShown(1);
  };

  const categories = useMemo(() => {
    const base = getMediaTemplateCategories(templates);
    return newTemplates.length
      ? [
          { category: NEW_TEMPLATE_CATEGORY, count: newTemplates.length },
          ...base,
        ]
      : base;
  }, [templates, newTemplates]);

  const visibleTemplates = useMemo(
    () =>
      category === NEW_TEMPLATE_CATEGORY
        ? newTemplates
        : filterMediaTemplatesByCategory(templates, category),
    [templates, newTemplates, category],
  );

  const limit = pageSize ? pageSize * pagesShown : visibleTemplates.length;
  const hasMore = limit < visibleTemplates.length;

  const tr = (key: string) => {
    const translated = tMedia(key);
    return translated !== key ? translated : t(key);
  };

  return (
    <div>
      <TemplateCategoryFilter
        categories={categories}
        value={category}
        onChange={setCategory}
        total={templates.length}
        className='mb-4'
      />
      <TemplateMasonry
        templates={visibleTemplates}
        getText={(template) => ({
          title: tr(template.title),
          description: tr(template.description),
        })}
        ctaLabel={tMedia('Use template')}
        isHidden={(_, index) => index >= limit}
        opensInNewTab={
          currentPath
            ? (template) => !template.uri.startsWith(currentPath)
            : undefined
        }
      />
      {hasMore && (
        <div className='mt-2 flex justify-center'>
          <button
            type='button'
            onClick={() => setPagesShown((pages) => pages + 1)}
            className='rounded-full border border-gray-200 bg-white px-5 py-2 text-sm font-medium text-gray-700 transition hover:border-gray-300 hover:text-gray-900 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:text-white'
          >
            {tMedia('Show more templates')} ({visibleTemplates.length - limit})
          </button>
        </div>
      )}
    </div>
  );
};

export default TemplatesGallery;
